import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API, formatApiError, formatDate, formatRelative, openAuthedPdf } from "../lib/api";
import { fmtMoney } from "../components/ops/opsUtils";
import {
  ArrowLeft, Download, Mail, RefreshCw, CheckCircle2, XCircle, Clock,
  FileText, Edit3, Save, RotateCcw, Send, AlertTriangle, History as HistoryIcon, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const STATUS_BADGES = {
  active:  { bg: "#DCFCE7", text: "#166534", label: "Active",   Icon: Clock },
  expired: { bg: "#FEE2E2", text: "#B91C1C", label: "Expired",  Icon: XCircle },
  voided:  { bg: "#F3F4F6", text: "#4B5563", label: "Voided",   Icon: XCircle },
  settled: { bg: "#DBEAFE", text: "#1E40AF", label: "Settled",  Icon: CheckCircle2 },
};

export default function PayoffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payoff, setPayoff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingOverride, setEditingOverride] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ to: "", cc: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/payoffs/${id}`);
      setPayoff(data);
      setOverrideAmount(data.override_amount != null ? String(data.override_amount) : "");
      setOverrideReason(data.override_reason || "");
      setEmailDraft((d) => ({ ...d, to: d.to || data.attorney_email || "" }));
    } catch (e) { toast.error(formatApiError(e)); navigate("/admin/payoffs"); }
    finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    try { await openAuthedPdf(`/operations/payoffs/${id}/pdf`, `${payoff?.statement_number || id}.pdf`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveOverride = async () => {
    setSavingOverride(true);
    try {
      const body = {
        override_amount: overrideAmount.trim() === "" ? null : Number(overrideAmount),
        override_reason: overrideReason || "",
      };
      await api.patch(`/operations/payoffs/${id}/override`, body);
      toast.success(body.override_amount == null ? "Reverted to calculated amount" : "Override saved");
      setEditingOverride(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingOverride(false); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const to = emailDraft.to.split(",").map((s) => s.trim()).filter(Boolean);
      const cc = emailDraft.cc.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post(`/operations/payoffs/${id}/send`, { to, cc, message: emailDraft.message });
      const recipients = data.recipients || to;
      toast.success(`Queued for delivery to ${recipients.join(", ")}`);
      setShowEmailDialog(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Generate a fresh payoff at today's balance? Original will be kept for audit.")) return;
    try {
      const { data } = await api.post(`/operations/payoffs/${id}/regenerate`);
      toast.success(`New statement ${data.statement_number} generated`);
      navigate(`/admin/payoffs/${data.payoff_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markSettled = async () => {
    if (!window.confirm("Mark this payoff as Settled? Underlying loans will be closed at the final amount.")) return;
    try {
      await api.post(`/operations/payoffs/${id}/mark-settled`);
      toast.success("Payoff marked as settled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const voidPayoff = async () => {
    if (!window.confirm("Void this payoff? It can no longer be sent.")) return;
    try {
      await api.post(`/operations/payoffs/${id}/void`);
      toast.success("Payoff voided");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="payoff-loading">Loading payoff statement…</div>;
  if (!payoff) return null;

  const status = payoff.status || "active";
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.active;
  const StatusIcon = cfg.Icon;
  const hasOverride = payoff.override_amount != null;
  const calculated = payoff.calculated_payoff || 0;
  const final = payoff.final_amount || 0;
  const savings = hasOverride ? calculated - final : 0;
  const allTranches = payoff.tranches || [];

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="payoff-detail">
      <Link to="/admin/payoffs" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0A2540] mb-3" data-testid="back-to-payoffs">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All payoffs
      </Link>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#0066FF] rounded-2xl p-6 md:p-8 text-white mb-6" data-testid="payoff-hero">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#BFD7FF] mb-2 flex items-center gap-2">
              <FileText className="w-3 h-3" /> Payoff statement {payoff.statement_number}
            </div>
            <div className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{payoff.client_name}</div>
            <div className="text-sm text-[#BFD7FF] mt-1">
              {payoff.attorney_name || payoff.attorney_email}{payoff.attorney_firm ? ` · ${payoff.attorney_firm}` : ""}
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ background: cfg.bg, color: cfg.text }}>
            <StatusIcon className="w-3.5 h-3.5" /> {cfg.label}
          </span>
        </div>
        <div className="grid sm:grid-cols-3 gap-5 pt-5 border-t border-white/15">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#BFD7FF] mb-1">Amount due</div>
            <div className="font-display text-4xl md:text-5xl font-semibold tabular-nums" data-testid="payoff-final-amount">{fmtMoney(final)}</div>
            {hasOverride && (
              <div className="text-[11px] text-[#BFD7FF] mt-1.5">
                Reduced <span className="text-white font-medium">{fmtMoney(savings)}</span> from {fmtMoney(calculated)}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#BFD7FF] mb-1">Valid through</div>
            <div className="text-xl font-medium tabular-nums">{formatDate(payoff.valid_through)}</div>
            <div className="text-[11px] text-[#BFD7FF] mt-1.5">Issued {formatRelative(payoff.generated_at)} by {payoff.generated_by}</div>
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end items-start">
            <Button onClick={downloadPdf} variant="secondary" className="bg-white text-[#0A2540] hover:bg-[#F9FAFB] h-9" data-testid="download-pdf-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
            </Button>
            <Button onClick={() => setShowEmailDialog(true)} className="bg-white/15 hover:bg-white/25 text-white h-9" data-testid="send-email-btn">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-5">
          {/* Breakdown */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-baseline justify-between">
              <div className="font-medium text-[#0A2540]">Funding breakdown</div>
              <span className="text-xs text-[#6B7280]">{allTranches.length} tranche{allTranches.length !== 1 ? "s" : ""}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <th className="px-4 py-2.5 font-medium">#</th>
                  <th className="px-4 py-2.5 font-medium">Funded</th>
                  <th className="px-4 py-2.5 font-medium text-right">Principal</th>
                  <th className="px-4 py-2.5 font-medium text-right">Rate</th>
                  <th className="px-4 py-2.5 font-medium text-right">Months</th>
                  <th className="px-4 py-2.5 font-medium text-right">Accrued</th>
                  <th className="px-4 py-2.5 font-medium text-right">Payoff</th>
                </tr>
              </thead>
              <tbody>
                {allTranches.map((tr, i) => (
                  <tr key={`${tr.loan_id}_${tr.id}`} className="border-b border-[#F3F4F6]" data-testid={`tranche-row-${i}`}>
                    <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{i + 1}</td>
                    <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(tr.funding_date)}</td>
                    <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums">{fmtMoney(tr.principal)}</td>
                    <td className="px-4 py-2.5 text-right text-[#6B7280] tabular-nums">{((tr.monthly_rate || 0) * 100).toFixed(2)}%</td>
                    <td className="px-4 py-2.5 text-right text-[#6B7280] tabular-nums">{(tr.months_elapsed || 0).toFixed(1)}</td>
                    <td className="px-4 py-2.5 text-right text-[#047857] tabular-nums">{fmtMoney(tr.accrued)}</td>
                    <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums font-medium">{fmtMoney(tr.payoff_amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#F9FAFB]">
                <tr className="text-sm">
                  <td colSpan={2} className="px-4 py-3 text-[11px] uppercase tracking-wider text-[#6B7280]">Totals</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtMoney(payoff.total_principal)}</td>
                  <td colSpan={2} />
                  <td className="px-4 py-3 text-right tabular-nums text-[#047857]">{fmtMoney(payoff.total_gross_payoff - payoff.total_principal)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtMoney(payoff.total_gross_payoff)}</td>
                </tr>
              </tfoot>
            </table>
            {(payoff.total_paydowns > 0 || payoff.total_reductions > 0) && (
              <div className="px-5 py-3 bg-[#F9FAFB] border-t border-[#E5E7EB] text-sm">
                {payoff.total_paydowns > 0 && <div className="flex justify-between text-[#374151]"><span>Less: Client paydowns</span><span className="tabular-nums">− {fmtMoney(payoff.total_paydowns)}</span></div>}
                {payoff.total_reductions > 0 && <div className="flex justify-between text-[#374151]"><span>Less: Prior reductions</span><span className="tabular-nums">− {fmtMoney(payoff.total_reductions)}</span></div>}
                <div className="flex justify-between mt-1.5 pt-1.5 border-t border-[#E5E7EB] text-[#0A2540] font-medium"><span>Calculated payoff</span><span className="tabular-nums">{fmtMoney(payoff.calculated_payoff)}</span></div>
              </div>
            )}
          </div>

          {/* Override card */}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="override-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium text-[#0A2540]">Negotiated payoff override</div>
                <div className="text-xs text-[#6B7280]">{hasOverride ? "Final amount on PDF reflects the override only." : "Apply a negotiated reduction to replace the calculated payoff."}</div>
              </div>
              {!editingOverride && (
                <Button variant="outline" onClick={() => setEditingOverride(true)} className="h-9" data-testid="edit-override-btn">
                  <Edit3 className="w-3.5 h-3.5 mr-1.5" /> {hasOverride ? "Edit" : "Add override"}
                </Button>
              )}
            </div>
            {editingOverride ? (
              <div className="space-y-2 pt-2 border-t border-[#F3F4F6]">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Override amount (blank = revert)</Label>
                    <Input type="number" step="0.01" placeholder={fmtMoney(calculated)} value={overrideAmount} onChange={(e) => setOverrideAmount(e.target.value)} data-testid="override-amount-input" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Reason</Label>
                    <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g. negotiated reduction" data-testid="override-reason-input" className="mt-1" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" onClick={() => { setEditingOverride(false); setOverrideAmount(payoff.override_amount != null ? String(payoff.override_amount) : ""); }} data-testid="cancel-override-btn">Cancel</Button>
                  {hasOverride && (
                    <Button variant="outline" onClick={() => { setOverrideAmount(""); }} data-testid="clear-override-btn">
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Revert
                    </Button>
                  )}
                  <Button onClick={saveOverride} disabled={savingOverride} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="save-override-btn">
                    {savingOverride ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />} Save
                  </Button>
                </div>
              </div>
            ) : hasOverride ? (
              <div className="bg-[#FFF7E6] border border-[#FCD37C] rounded-md p-3 text-sm" data-testid="override-active">
                <div className="flex items-center gap-2 text-[#B45309] font-medium mb-1"><AlertTriangle className="w-4 h-4" /> Override in effect</div>
                <div className="text-[#0A2540]">
                  Calculated: <span className="line-through text-[#6B7280]">{fmtMoney(calculated)}</span> → Final <span className="font-semibold">{fmtMoney(final)}</span>{savings ? ` · Reduction of ${fmtMoney(savings)}` : ""}
                </div>
                {payoff.override_reason && <div className="text-xs text-[#6B7280] mt-1.5">Reason: {payoff.override_reason}</div>}
              </div>
            ) : (
              <div className="text-sm text-[#6B7280] italic">No override applied. The calculated payoff of <span className="text-[#0A2540] font-medium not-italic">{fmtMoney(calculated)}</span> is on the PDF.</div>
            )}
          </div>

          {/* Override history */}
          {(payoff.override_history?.length || 0) > 0 && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
              <div className="font-medium text-[#0A2540] mb-3 flex items-center gap-2"><HistoryIcon className="w-3.5 h-3.5" /> Override history</div>
              <div className="space-y-2">
                {payoff.override_history.slice().reverse().map((h, i) => (
                  <div key={`oh-${h.at || i}`} className="text-xs flex items-start gap-2 pb-2 border-b border-[#F3F4F6] last:border-b-0" data-testid={`override-history-${i}`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#B45309] mt-1.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-[#0A2540]">
                        {h.from == null ? <span className="text-[#6B7280]">No override</span> : <span className="tabular-nums">{fmtMoney(h.from)}</span>}
                        {" → "}
                        {h.to == null ? <span className="text-[#6B7280]">Reverted</span> : <span className="font-medium tabular-nums">{fmtMoney(h.to)}</span>}
                      </div>
                      {h.reason && <div className="text-[#6B7280] mt-0.5">{h.reason}</div>}
                      <div className="text-[10px] text-[#9CA3AF] mt-0.5 tabular-nums">{formatRelative(h.at)} · {h.by}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email log */}
          {(payoff.email_log?.length || 0) > 0 && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
              <div className="font-medium text-[#0A2540] mb-3 flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Email history</div>
              <div className="space-y-3">
                {payoff.email_log.slice().reverse().map((log, i) => (
                  <div key={`el-${log.sent_at || i}`} className="text-xs pb-3 border-b border-[#F3F4F6] last:border-b-0" data-testid={`email-log-${i}`}>
                    <div className="text-[#0A2540]">
                      Sent to <span className="font-medium">{log.to.join(", ")}</span>
                      {log.cc?.length > 0 && <span className="text-[#6B7280]"> · cc {log.cc.join(", ")}</span>}
                    </div>
                    <div className="text-[#6B7280] mt-0.5">{log.subject}</div>
                    <div className="text-[10px] text-[#9CA3AF] mt-0.5 tabular-nums">{formatRelative(log.sent_at)} by {log.sent_by}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Underlying loans</div>
            <div className="space-y-2">
              {(payoff.loan_breakdowns || []).map((b) => (
                <Link key={b.loan_id} to={`/admin/loans/${b.loan_id}`} className="block p-2.5 rounded-md hover:bg-[#F9FAFB] transition-colors" data-testid={`loan-link-${b.loan_id}`}>
                  <div className="text-sm font-medium text-[#0066FF] tabular-nums">{b.loan_id}</div>
                  <div className="text-xs text-[#6B7280]">{fmtMoney(b.total_principal)} principal · {fmtMoney(b.total_payoff)} payoff</div>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">Quick actions</div>
            <Button onClick={regenerate} variant="outline" className="w-full justify-start h-9" data-testid="regenerate-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-2" /> Regenerate at today's balance
            </Button>
            {status === "active" && (
              <Button onClick={markSettled} variant="outline" className="w-full justify-start h-9 text-[#166534] border-[#A7F3D0] hover:bg-[#F0FDF4]" data-testid="mark-settled-btn">
                <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Mark as settled
              </Button>
            )}
            {status !== "voided" && status !== "settled" && (
              <Button onClick={voidPayoff} variant="outline" className="w-full justify-start h-9 text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2]" data-testid="void-btn">
                <XCircle className="w-3.5 h-3.5 mr-2" /> Void this payoff
              </Button>
            )}
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Statement details</div>
            <Row label="Statement #" value={payoff.statement_number} />
            <Row label="Generated" value={formatDate(payoff.generated_at)} />
            <Row label="By" value={payoff.generated_by} />
            <Row label="Valid through" value={formatDate(payoff.valid_through)} />
            {payoff.notes && <Row label="Notes" value={payoff.notes} stack />}
          </div>
        </aside>
      </div>

      {/* Email dialog */}
      {showEmailDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A2540]/40 backdrop-blur-sm" onClick={() => setShowEmailDialog(false)} data-testid="email-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-[#E5E7EB] flex items-center justify-between">
              <div className="font-medium text-[#0A2540] text-sm flex items-center gap-2"><Send className="w-4 h-4 text-[#0066FF]" /> Email payoff PDF</div>
              <button onClick={() => setShowEmailDialog(false)} className="text-[#9CA3AF] hover:text-[#0A2540]" data-testid="email-dialog-close">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="text-xs">To (comma-separated)</Label>
                <Input value={emailDraft.to} onChange={(e) => setEmailDraft({ ...emailDraft, to: e.target.value })} data-testid="email-to-input" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Cc (optional)</Label>
                <Input value={emailDraft.cc} onChange={(e) => setEmailDraft({ ...emailDraft, cc: e.target.value })} data-testid="email-cc-input" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Additional message (optional)</Label>
                <Textarea rows={3} value={emailDraft.message} onChange={(e) => setEmailDraft({ ...emailDraft, message: e.target.value })} data-testid="email-message-input" className="mt-1" />
              </div>
              <div className="text-xs text-[#6B7280] bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-2.5">
                The PDF will be attached automatically. Internal copy goes to aaron@instabridgefunding.com.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#E5E7EB] flex justify-end gap-2 bg-[#F9FAFB]">
              <Button variant="ghost" onClick={() => setShowEmailDialog(false)} data-testid="email-cancel">Cancel</Button>
              <Button onClick={sendEmail} disabled={sending || !emailDraft.to.trim()} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="email-send-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Send PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, stack }) {
  return (
    <div className={`py-1.5 ${stack ? "" : "flex justify-between"} text-sm border-t border-[#F3F4F6] first:border-t-0`}>
      <span className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</span>
      <span className={`text-[#0A2540] ${stack ? "block mt-1 text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}
